import * as TabsPrimitive from "@radix-ui/react-tabs";
import { useRouter, useRouterState } from "@tanstack/react-router";
import type { JSX, ReactNode } from "react";
import { v3ChipVariants } from "@/components/ui/v3/chip";
import { cn } from "@/lib/utils";
import { CHIP, CHIP_ROW } from "./page";

export interface PillTab {
  value: string;
  label: string;
  content: ReactNode;
  icon?: ReactNode;
}

// Pill tabs kept in the URL hash (#library, #shortcuts, …), so links and
// reloads land on the same tab, as with the classic HashTabs.
export const PillHashTabs = ({
  tabs,
  label,
  className,
  v3 = false,
}: {
  tabs: PillTab[];
  label: string;
  className?: string;
  // v3.2 chips (ink when active) instead of the redesign pills.
  v3?: boolean;
}): JSX.Element => {
  const router = useRouter();
  const hash = useRouterState({
    select: (state) => state.location.hash.replace("#", "").trim(),
  });
  const active = tabs.some((tab) => tab.value === hash)
    ? hash
    : (tabs[0]?.value ?? "");

  return (
    <TabsPrimitive.Root
      className={cn("space-y-5 sm:space-y-6", className)}
      value={active}
      onValueChange={(value) => {
        void router.navigate({ hash: value, replace: true } as never);
      }}
    >
      <TabsPrimitive.List
        aria-label={label}
        className={cn(CHIP_ROW, "max-sm:gap-1.5")}
      >
        {tabs.map((tab) => (
          <TabsPrimitive.Trigger
            key={tab.value}
            className={cn(
              v3 ? v3ChipVariants() : CHIP,
              "max-sm:flex-auto max-sm:justify-center max-sm:px-3 max-sm:text-[13px] [&_svg]:size-4 max-sm:[&_svg]:hidden",
            )}
            data-testid={`tab-${tab.value}`}
            value={tab.value}
          >
            {tab.icon}
            {tab.label}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
      {tabs.map((tab) => (
        <TabsPrimitive.Content
          key={tab.value}
          className="focus-visible:outline-hidden"
          value={tab.value}
        >
          {tab.content}
        </TabsPrimitive.Content>
      ))}
    </TabsPrimitive.Root>
  );
};
